import React from "react";
import { Sparkles } from "lucide-react";

/**
 * One line on the dashboard telling a trial org how long it has left.
 *
 * The dashboard used to carry the whole plan-details grid. That is a thing you
 * go and look at, not something you need above your job roles every day, so it
 * lives on the Usage page now and only this stays: the fact with a deadline,
 * and the way to act on it.
 *
 * Renders nothing at all for paid organizations.
 */

const daysUntil = (endDate) => {
  if (!endDate) return null;
  const diff = new Date(endDate).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

const TrialBanner = ({ data, onUpgrade }) => {
  const subscription = data?.subscription;
  // is_trial is set explicitly on every subscription the code creates, so an
  // exact check is safe and paid orgs never see this.
  if (!subscription || subscription.is_trial !== true) return null;

  const days = subscription.days_remaining ?? daysUntil(subscription.end_date);

  let message;
  if (days === null) message = "You're on a free trial.";
  else if (days === 0) message = "Your free trial ends today.";
  else if (days === 1) message = "Your free trial ends tomorrow.";
  else message = `Your free trial ends in ${days} days.`;

  // Only the last few days are worth colouring; before that it is information,
  // not a warning.
  const urgent = days !== null && days <= 3;

  return (
    <div
      className={`w-full px-5 py-3.5 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
        urgent
          ? "bg-[#FFF4F4] border border-[#F6D4D4] dark:bg-red-950/20 dark:border-red-900/40"
          : "bg-bgSecondary dark:bg-gradient-to-r dark:from-[#232142]/40 dark:to-[#7E64A3]/30"
      }`}
    >
      <div className="flex flex-col">
        <span
          className={`text-[15px] font-semibold ${
            urgent ? "text-[#B23B3B] dark:text-red-300" : "text-[#404040] dark:text-[#AEB9E1]"
          }`}
        >
          {message}
        </span>
        <span className="text-[12px] text-gray-500 dark:text-gray-400">
          Upgrade to keep your job roles, candidates and reports.
        </span>
      </div>

      <button
        onClick={onUpgrade}
        className="shrink-0 px-4 py-2 rounded-full bg-gradient-to-r from-[#4D76F8] to-[#E354B4] text-white font-semibold hover:opacity-90 transition-opacity text-[13px] flex items-center justify-center gap-1.5 shadow-sm"
      >
        <Sparkles className="w-4 h-4" strokeWidth={2} />
        Upgrade Now
      </button>
    </div>
  );
};

export default TrialBanner;
